import axios from 'axios';
import _ from 'lodash';

import { Common } from './common';
import {
  CreateValues,
  Feed,
  FieldMapping,
  FindAllOptions,
  ImportValidation,
  SyncJob,
  UpdateValues,
  Upload,
  UploadLog,
} from './models/feed';
import { ImportError } from './models/provider';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(query: FindAllOptions = {}): Promise<Feed[]> {
    const url = this.common.constructPath('/feeds', query);
    return this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<Feed> {
    const url = this.common.constructPath(`/feeds/${id}`);
    return this.common.getJSON(url);
  }

  async create(toAdd: CreateValues): Promise<Feed> {
    const url = this.common.constructPath('/feeds');
    return this.common.postJSON(url, toAdd);
  }

  async update(id: string, toUpdate: UpdateValues): Promise<Feed> {
    const url = this.common.constructPath(`/feeds/${id}`);
    return this.common.patchJSON(url, toUpdate);
  }

  async remove(id: string): Promise<boolean> {
    const url = this.common.constructPath(`/feeds/${id}`);
    const { success } = await this.common.deleteJSON(url);
    return success;
  }

  async sync(
    id: string,
  ): Promise<{ success: boolean; jobs: number; practices: number }> {
    const url = this.common.constructPath(`/feeds/${id}/sync`);
    return this.common.postJSON(url, {});
  }

  async import(args: {
    id: string;
    practiceId: string;
    csv: string;
    save?: boolean;
    append?: boolean;
    remove?: boolean;
  }): Promise<
    {
      entry: Record<string, string>;
      validation: Record<
        string,
        { value: string; error?: ImportError; errors?: string[] }
      >;
    }[]
  > {
    const { id, ...body } = args;
    const url = this.common.constructPath(`/feeds/${id}/import`);
    return this.common.postJSON(url, body);
  }

  async findAllUploads(feedId: string): Promise<Upload[]> {
    const url = this.common.constructPath(`/feeds/${feedId}/uploads`);
    return this.common.getJSON(url);
  }

  async findUploadByFeedIdAndUploadId(
    feedId: string,
    uploadId: string,
  ): Promise<Upload> {
    const url = this.common.constructPath(
      `/feeds/${feedId}/uploads/${uploadId}`,
    );
    return this.common.getJSON(url);
  }

  async createUpload(args: {
    feedId: string;
    csvGsPath: string;
  }): Promise<Upload> {
    const { feedId, csvGsPath } = args;
    const url = this.common.constructPath(`/feeds/${feedId}/uploads`);
    return this.common.postJSON(url, { csvGsPath });
  }

  async getUploadUrl(feedId: string): Promise<{ url: string; path: string }> {
    const url = this.common.constructPath(
      `/feeds/${feedId}/uploads/signed-url`,
    );
    return this.common.postJSON(url, {});
  }

  async upload(url: string, file: File) {
    return axios.put(url, file, {
      headers: { 'Content-Type': 'text/csv' },
      timeout: 30000,
    });
  }

  async getUploadLogs(feedId: string, uploadId: string): Promise<UploadLog[]> {
    const url = this.common.constructPath(
      `/feeds/${feedId}/uploads/${uploadId}/logs`,
    );
    const { logs } = await this.common.getJSON(url);
    return logs;
  }

  getUploadDownloadUrl(args: { feedId: string; uploadId: string }) {
    return this.common.constructPath(
      `/feeds/${args.feedId}/uploads/${args.uploadId}/file`,
    );
  }

  async findDataBindings(query: { job: SyncJob }): Promise<string[]> {
    const url = this.common.constructPath(`/feeds/data-bindings`, query);
    return this.common.getJSON(url);
  }

  async findAllMappings(feedId: string): Promise<FieldMapping[]> {
    const url = this.common.constructPath(`/feeds/${feedId}/field-mappings`);
    const results: FieldMapping[] = await this.common.getJSON(url);
    return results.map((field) =>
      _.pick(field, ['field', 'dataBinding', 'transform']),
    );
  }

  async upsertMappings(
    feedId: string,
    toUpsert: FieldMapping[],
  ): Promise<FieldMapping[]> {
    const url = this.common.constructPath(`/feeds/${feedId}/field-mappings`);
    const results: FieldMapping[] = await this.common.putJSON(url, toUpsert);
    return results.map((field) =>
      _.pick(field, ['field', 'dataBinding', 'transform']),
    );
  }

  async uploadVerify(args: {
    feedId: string;
    rows: Record<string, string>[];
    practiceId?: string;
  }): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(
      `/feeds/${args.feedId}/uploads/import/verify`,
    );
    return this.common.postJSON(url, _.pick(args, 'rows', 'practiceId'));
  }

  async uploadTransform(args: {
    feedId: string;
    csv: string;
    practiceId?: string;
  }): Promise<Record<string, string>[]> {
    const url = this.common.constructPath(
      `/feeds/${args.feedId}/uploads/import/transform`,
    );
    return this.common.postJSON(url, _.pick(args, 'csv', 'practiceId'));
  }

  async uploadImport(args: {
    feedId: string;
    rows: Record<string, string>[];
    append?: boolean;
    remove?: boolean;
    practiceId?: string;
  }): Promise<{ feedUpload: Upload }> {
    const url = this.common.constructPath(
      `/feeds/${args.feedId}/uploads/import`,
    );
    return this.common.postJSON(
      url,
      _.pick(args, 'rows', 'append', 'remove', 'practiceId'),
    );
  }
}
